import React, { Component } from "react";
import classnames from "classnames";
import equal from "fast-deep-equal/es6";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { debounce } from "@formant/core/common/debounce";
import { range } from "@formant/core/common/range";
import { duration } from "@formant/core/model/duration";
import { ITeleopJoystickConfiguration } from "@formant/core/model/ITeleopJoystickConfiguration";
import { IVector2 } from "@formant/core/model/IVector2";
import { Icon } from "@formant/ui/component/common/Icon";

import { ftueStore, operateStore } from "../../../../../stores";
import { clamp } from "../../../../../utils/clamp";
import {
    defaultNewValueJoystickDebounce,
    defaultSameValueJoystickDebounce,
} from "../../../../../utils/joystickDebounce";
import { Heading3 } from "../../../../common/Heading3";
import { Timer } from "../../../../common/Timer";
import { WindowEvent } from "../../../../common/WindowEvent";
import { TeleopActions } from "../../../TeleopActions";
import { ITeleopTwistValue } from "../ITeleopTwistValue";
import { deadzone } from "./deadzone";
import * as styles from "./index.scss";
import { Labels } from "./Labels";

export interface ITelepJoystickModuleProps {
    joystickConfiguration: ITeleopJoystickConfiguration;
    className?: string;
    debug?: boolean;
    armed: boolean;
    onSendTwistValues: (
        twistValues: ITeleopTwistValue[],
        reliable: boolean,
    ) => void;
}

@observer
export class TeleopJoystick extends Component<ITelepJoystickModuleProps> {
    @observable
    private mouseDown = false;
    @observable
    private dotCoordinates?: IVector2;
    @observable
    private active: boolean = false;
    private joystickCoordinates?: IVector2;
    private pad?: HTMLElement;
    private label: React.RefObject<HTMLDivElement> = React.createRef();

    private up: IVector2 = { x: 0, y: 0 };
    private down: IVector2 = { x: 0, y: 0 };
    private left: IVector2 = { x: 0, y: 0 };
    private right: IVector2 = { x: 0, y: 0 };
    private xAxis: IVector2 = { x: 0, y: 0 };
    private yAxis: IVector2 = { x: 0, y: 0 };

    private startTarget?: EventTarget;
    private lastValues: ITeleopTwistValue[] = [];
    private canSendSameValue: boolean = true;
    private canSendNewValue: boolean = true;

    private sameValueCooldown = debounce(
        () => (this.canSendSameValue = true),
        (this.props.joystickConfiguration.sameValueDebounce ??
            defaultSameValueJoystickDebounce) * duration.second,
    );
    private newValueCooldown = debounce(
        () => (this.canSendNewValue = true),
        (this.props.joystickConfiguration.newValueDebounce ??
            defaultNewValueJoystickDebounce) * duration.second,
    );

    public componentDidMount() {
        this.pad?.addEventListener("contextmenu", this.onContextMenu);
    }

    public componentWillUnmount() {
        this.pad?.removeEventListener("contextmenu", this.onContextMenu);
    }

    public componentDidUpdate(prevProps: ITelepJoystickModuleProps) {
        const { armed } = this.props;
        if (armed === prevProps.armed) {
            return;
        }

        if (armed) {
            this.addInputs(true);
        } else {
            this.joystickCoordinates = { x: 0, y: 0 };
            this.active = false;
            this.sendCommand({
                coordinates: this.joystickCoordinates,
                reliable: true,
            });
        }
    }

    public render() {
        const { className, debug, joystickConfiguration } = this.props;
        const { armed } = this.props;
        const { position } = joystickConfiguration;
        const { gamepad } = operateStore;
        const { joystick: ftue } = ftueStore;
        return (
            <div
                className={classnames(
                    styles["teleop-joystick"],
                    debug && styles.debug,
                    ftue && styles.ftue,
                    className,
                )}
                data-testid="teleop-joystick"
            >
                {position === "left" && (
                    <TeleopActions
                        configuration={joystickConfiguration}
                        onLeftJoystickUp={this.onUp}
                        onLeftJoystickDown={this.onDown}
                        onLeftJoystickLeft={this.onLeft}
                        onLeftJoystickRight={this.onRight}
                        onLeftJoystickPressed={this.onJoystickPressed}
                        onLeftJoystickReleased={this.onJoystickReleased}
                        onLeftJoystickXAxis={this.onXAxis}
                        onLeftJoystickYAxis={this.onYAxis}
                        onStop={this.onStop}
                    />
                )}
                {position === "right" && (
                    <TeleopActions
                        configuration={joystickConfiguration}
                        onRightJoystickUp={this.onUp}
                        onRightJoystickDown={this.onDown}
                        onRightJoystickLeft={this.onLeft}
                        onRightJoystickRight={this.onRight}
                        onRightJoystickPressed={this.onJoystickPressed}
                        onRightJoystickReleased={this.onJoystickReleased}
                        onRightJoystickXAxis={this.onXAxis}
                        onRightJoystickYAxis={this.onYAxis}
                        onStop={this.onStop}
                    />
                )}
                <WindowEvent
                    type="touchmove"
                    onEvent={this.onTouchMove}
                    options={{ passive: false }}
                />
                <WindowEvent type="mouseup" onEvent={this.onMouseUp} />
                <WindowEvent type="mousemove" onEvent={this.onMouseMove} />
                <WindowEvent type="touchend" onEvent={this.onTouchEnd} />
                {debug && (
                    <div className={styles.info}>
                        <Heading3>{position}</Heading3>
                        <div className={styles.row}>
                            <div className={styles.heading}>Mouse</div>
                            <div className={styles.value}>
                                {this.mouseDown.toString()}
                            </div>
                        </div>
                        <div className={styles.row}>
                            <div className={styles.heading}>Gamepad</div>
                            <div className={styles.value}>
                                {(!!gamepad).toString()}
                            </div>
                        </div>
                    </div>
                )}

                <div
                    className={classnames(
                        styles.pad,
                        (this.mouseDown || this.active) && styles.active,
                    )}
                    ref={_ => (this.pad = _ || undefined)}
                    onMouseDown={this.onMouseDown}
                    onTouchStart={this.onTouchStart}
                >
                    <Timer
                        interval={duration.second * 0.05}
                        onTick={this.onTick}
                    />
                    <div className={styles.border} />
                    <Icon
                        name="joystick-star"
                        className={styles.star}
                        size="48"
                    />
                    <Labels
                        targetRef={this.label}
                        position={position}
                        className={styles.labels}
                        armed={armed}
                        onStop={this.onStop}
                    />
                    <div
                        className={classnames(styles.dot)}
                        style={{
                            top: this.dotCoordinates?.y,
                            left: this.dotCoordinates?.x,
                        }}
                    />
                </div>
            </div>
        );
    }

    private addInputs(reliable: boolean) {
        const { armed } = this.props;
        const { up, down, left, right, xAxis, yAxis } = this;

        if (this.mouseDown) {
            return;
        }

        const coords = armed
            ? [up, down, left, right, xAxis, yAxis].reduce((agg, _) =>
                  addVectors(agg, _),
              )
            : { x: 0, y: 0 };

        this.joystickCoordinates = coords;
        this.active = vectorLength(coords) > 0;

        this.sendCommand({
            coordinates: this.joystickCoordinates,
            reliable,
        });
    }

    private onTryToInteract = () => {
        operateStore.tryingToInteract = true;
        setTimeout(() => {
            operateStore.tryingToInteract = false;
        }, 300);
    };

    private onUp = (value: number) => {
        this.up = { x: 0, y: value };
        if (!this.props.armed) {
            this.onTryToInteract();
        }
    };

    private onDown = (value: number) => {
        this.down = { x: 0, y: -value };
        if (!this.props.armed) {
            operateStore.tryingToInteract = true;
            this.onTryToInteract();
        }
    };

    private onLeft = (value: number) => {
        this.left = { x: -value, y: 0 };
        if (!this.props.armed) {
            operateStore.tryingToInteract = true;
            this.onTryToInteract();
        }
    };

    private onRight = (value: number) => {
        this.right = { x: value, y: 0 };
        if (!this.props.armed) {
            operateStore.tryingToInteract = true;
            this.onTryToInteract();
        }
    };

    private onXAxis = (value: number) => {
        this.xAxis = { x: clamp(deadzone(value)), y: 0 };
        this.addInputs(false);
    };

    private onYAxis = (value: number) => {
        this.yAxis = { x: 0, y: clamp(deadzone(value)) * -1 };
        this.addInputs(false);
    };

    private onJoystickPressed = () => {
        this.addInputs(true);
    };

    private onJoystickReleased = () => {
        this.addInputs(true);
    };

    private onContextMenu = (event: MouseEvent) => {
        event.preventDefault();
    };

    private onMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
        const { clientX: x, clientY: y, target } = event;
        if (event.button !== 0) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        this.mouseDown = true;
        this.startTarget = target;
        this.setJoystickCoordinates(x, y);
        this.sendCommand({
            coordinates: this.joystickCoordinates,
            reliable: false,
        });
    };

    private onMouseUp = () => {
        this.mouseDown = false;
        this.joystickCoordinates = { x: 0, y: 0 };
        if (this.startTarget) {
            this.sendCommand({
                coordinates: this.joystickCoordinates,
                reliable: true,
            });
        }
        this.startTarget = undefined;
    };

    private onMouseMove = (event: MouseEvent) => {
        const { clientX: x, clientY: y } = event;
        if (event.button !== 0 || !this.mouseDown) {
            return;
        }
        this.setJoystickCoordinates(x, y);
    };

    private onTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
        const { touches } = event;
        const touchList = range(0, touches.length)
            .map(_ => touches.item(_))
            .filter(
                _ => _?.target === this.pad || _?.target === this.label.current,
            );

        if (!touchList[0]) {
            return;
        }
        const { clientX: x, clientY: y, target } = touchList[0];
        this.mouseDown = true;
        this.startTarget = target;
        this.setJoystickCoordinates(x, y);
        this.sendCommand({
            coordinates: this.joystickCoordinates,
            reliable: false,
        });
    };

    private onTouchEnd = (event: TouchEvent) => {
        if (event.target !== this.startTarget) {
            return;
        }

        this.mouseDown = false;
        this.joystickCoordinates = { x: 0, y: 0 };
        this.sendCommand({
            coordinates: this.joystickCoordinates,
            reliable: true,
        });
        this.startTarget = undefined;
    };

    private onTouchMove = (event: TouchEvent) => {
        if (event.target !== this.startTarget) {
            return;
        }

        const { touches } = event;
        const touchList = range(0, touches.length)
            .map(_ => touches.item(_))
            .filter(
                _ => _?.target === this.pad || _?.target === this.label.current,
            );

        if (!touchList[0]) {
            return;
        }
        const { clientX: x, clientY: y } = touchList[0];
        this.setJoystickCoordinates(x, y);
    };

    private setJoystickCoordinates(x: number, y: number) {
        const { armed } = this.props;
        const { pad } = this;
        if (!pad) {
            return;
        }

        const { top, left, height, width } = pad.getBoundingClientRect();

        const coords: IVector2 = armed
            ? {
                  x: clamp(((x - left) / width) * 2 - 1),
                  y: clamp(((y - top) / height) * -2 + 1),
              }
            : { x: 0, y: 0 };

        this.joystickCoordinates = {
            x: clamp(deadzone(coords.x)),
            y: clamp(deadzone(coords.y)),
        };

        const { x: clampedX, y: clampedY } = clampVectorLength(coords, 0.8);

        this.dotCoordinates = {
            x: ((clampedX + 1) / 2) * width,
            y: ((clampedY - 1) / -2) * height,
        };
    }

    private setDotFromJoystickCoordinates(joystickCoords: IVector2) {
        const { pad } = this;
        if (!pad) {
            return;
        }
        const { height, width } = pad.getBoundingClientRect();

        const { x, y } = clampVectorLength(joystickCoords, 0.8);

        this.dotCoordinates = {
            x: ((x + 1) / 2) * width,
            y: ((y - 1) / -2) * height,
        };
    }

    private sendCommand = (config: {
        coordinates?: IVector2;
        reliable: boolean;
    }) => {
        const { coordinates, reliable } = config;
        const { joystickConfiguration } = this.props;
        const { x, y } = coordinates || { x: 0, y: 0 };
        const outputDimensions: ITeleopTwistValue[] = [];
        const { joystick: ftue } = ftueStore;

        if (!joystickConfiguration) {
            return;
        }

        if (ftue) {
            ftueStore.joystick = false;
        }

        const { x: xAxis, y: yAxis } = joystickConfiguration;

        if (xAxis?.dimension) {
            outputDimensions.push({
                dimension: xAxis?.dimension,
                value:
                    Math.abs(Math.pow(Math.abs(x), xAxis.expo || 2)) *
                        (xAxis.scale || 1) *
                        Math.sign(x) || 0,
            });
        }

        if (yAxis?.dimension) {
            outputDimensions.push({
                dimension: yAxis.dimension,
                value:
                    Math.abs(Math.pow(Math.abs(y), yAxis.expo || 2)) *
                        (yAxis.scale || 1) *
                        Math.sign(y) || 0,
            });
        }

        if (this.joystickCoordinates && !this.mouseDown) {
            this.setDotFromJoystickCoordinates(this.joystickCoordinates);
        }

        const equivalent = equal(this.lastValues, outputDimensions);
        if (
            !reliable &&
            ((equivalent && !this.canSendSameValue) ||
                (!equivalent && !this.canSendNewValue))
        ) {
            return;
        }

        this.canSendSameValue = false;
        this.canSendNewValue = false;
        this.newValueCooldown();
        this.sameValueCooldown();
        this.lastValues = outputDimensions;

        this.props.onSendTwistValues(outputDimensions, reliable);
    };

    private onTick = () => {
        const { mouseDown, active, joystickCoordinates, sendCommand } = this;
        if (mouseDown || active) {
            sendCommand({
                coordinates: joystickCoordinates,
                reliable: false,
            });
        }
    };

    public onStop = () => {
        this.joystickCoordinates = { x: 0, y: 0 };
        this.active = false;
        this.sendCommand({
            coordinates: this.joystickCoordinates,
            reliable: false,
        });
    };
}

function addVectors(a: IVector2, b: IVector2) {
    return { x: a.x + b.x, y: a.y + b.y };
}

function vectorLength(v: IVector2) {
    const { x, y } = v;
    return Math.sqrt(x * x + y * y);
}

function clampVectorLength(v: IVector2, l: number) {
    const length = vectorLength(v);
    const { x, y } = v;
    if (length > l) {
        return { x: (x / length) * l, y: (y / length) * l };
    } else {
        return v;
    }
}
